import type * as THREE from 'three'
import { isFeatureResultMesh } from '../../mesh/feature'
import type { FeatureResultMesh } from '../../mesh/feature'
import { collectImportedModelTopologyRepositories } from '../../imported-model/topology/imported-model-topology-runtime'
import { ImportedModelInstancedTopologyDisplayEdges } from '../../imported-model/instancing/edge'

/**
 * 收集当前真正显示模型边的权威拓扑宿主，统一普通边和实例边的捕捉入口。
 *
 * 只在草图投影缓存重建时查询；不恢复被实例化替代的 source Mesh，不创建普通默认边，
 * 不缓存场景对象。按需创建的语义宿主仍由已有 Repository 负责变换投影和释放。
 */
export class VisibleModelEdgeProjectionSourceCollector {
  /** @param host 场景与实例表面的只读查询端口。 */
  public constructor(private readonly host: VisibleModelEdgeProjectionSourceCollectorHost) {}

  /** @returns 去重后的可见普通/实例模型拓扑宿主。 */
  public collect(): FeatureResultMesh[] {
    const scene = this.host.getScene()
    if (!scene) return []
    const result = new Set<FeatureResultMesh>()
    scene.traverse(object => {
      if (isFeatureResultMesh(object) && object.isTopologyDisplayEdgesVisible() && this.isVisible(object)) {
        result.add(object)
      }
    })
    for (const repository of collectImportedModelTopologyRepositories(scene)) {
      for (const id of repository.listBodyBindingIds()) {
        const record = repository.resolveBodyRecordByBindingId(id)
        if (!record) continue
        const surface = this.host.resolveInstancedSurface(record.visibleMesh)
        if (!surface || !this.isVisible(surface)) continue
        const hasVisibleEdges = surface.children.some(child => (
          child instanceof ImportedModelInstancedTopologyDisplayEdges && child.visible
        ))
        if (!hasVisibleEdges) continue
        // source 已脱离物理树且 visible=false，必须以实际实例边判断显隐，而非读取 source 的抑制状态。
        const topologyHost = repository.ensurePickHost(record)
        if (this.isVisible(topologyHost)) result.add(topologyHost)
      }
    }
    return [...result]
  }

  /**
   * 判断实际绘制对象及祖先是否可见。
   * @param object 场景中的普通对象或实例表面。
   * @returns 整条父链均可见时为 true。
   */
  private isVisible(object: THREE.Object3D): boolean {
    let current: THREE.Object3D | null = object
    while (current) {
      if (!current.visible) return false
      current = current.parent
    }
    return true
  }
}
